
import _ from 'lodash'
import { createContext, useContext } from 'react'

import { setToast } from '@/entities/toast'
import { setWidgetWindowInactive, updateActiveWidgetWindowId } from '@/entities/widgetWindows/widgetWindowsSlice'
import { createRemoveUpdate, updateTree, WidgetType, WidgetTypeWithSuffix } from '@/shared/components/Mosaic'
import { isToolWidgetType } from '@/shared/components/Mosaic/lib/isToolWidgetType'
import { getLeaves, getNumberOfLeaves, getPathToNode, isKeyExistedInTree } from '@/shared/components/Mosaic/lib/mosaicUtilities'
import { MosaicKey, MosaicNode } from '@/shared/components/Mosaic/models/mosaic'
import { MAX_WINDOW_COUNT } from '@/shared/config/consts'
import { useAppDispatch } from '@/shared/store'

export type MosaicLayoutContextType = {
  replaceWidgetWindow: (sourceWidgetId: MosaicKey, newWidgetId: WidgetTypeWithSuffix) => void
  closeWidgetWindows: (widgetId: WidgetTypeWithSuffix[]) => void
  spawnWidgetInTopRightCorner: (widgetId: WidgetTypeWithSuffix) => void
}


export const MosaicLayoutContext = createContext<MosaicLayoutContextType>({
  replaceWidgetWindow: () => { },
  closeWidgetWindows: () => { },
  spawnWidgetInTopRightCorner: () => { },
})

export const useMosaicLayoutContext = () => {
  const context = useContext(MosaicLayoutContext)

  if (context === undefined) {
    throw new Error(
      'useMosaicLayoutContext must be used within a MosaicLayoutContext.Provider',
    )
  }

  return context
}

export const useMosaicLayoutActions = (
  mosaicRootNode: MosaicNode<WidgetTypeWithSuffix> | null,
  setRootNode: (node: MosaicNode<WidgetTypeWithSuffix> | null) => void,
) => {
  const dispatch = useAppDispatch()

  // Replace the source widget with the new widget
  const replaceWidgetWindow = (sourceWidgetId: MosaicKey, newWidgetId: WidgetTypeWithSuffix) => {
    if (sourceWidgetId === newWidgetId) return

    const pathToSourceWidget = getPathToNode(mosaicRootNode, sourceWidgetId)

    if (!pathToSourceWidget) return

    if (mosaicRootNode === null) {
      setRootNode(newWidgetId)

      return
    }

    const updatedRootNode = updateTree(mosaicRootNode, [
      {
        path: pathToSourceWidget,
        spec: {
          $set: newWidgetId,
        },
      },
    ])

    dispatch(setWidgetWindowInactive(sourceWidgetId))
    dispatch(updateActiveWidgetWindowId(newWidgetId))
    setRootNode(updatedRootNode)
  }

  // Close the widget windows based on the widgetIds
  const closeWidgetWindows = (widgetIds: WidgetTypeWithSuffix[]) => {
    if (!mosaicRootNode) return
    if (widgetIds.length === 0) return

    // Continue to iterate through the widgetIds and apply update to the rootNode
    let rootNode = _.cloneDeep(mosaicRootNode)

    for (const widgetId of widgetIds) {
      const path = getPathToNode(rootNode, widgetId)

      if (!path) continue

      if (path?.length === 0) {
        setRootNode(null)

        return
      }

      rootNode = updateTree(rootNode, [createRemoveUpdate(rootNode, path)])
    }

    setRootNode(rootNode)
  }

  // Spawn a widget window on the top right corner based on the widgetId
  const spawnWidgetInTopRightCorner = (widgetId: WidgetTypeWithSuffix) => {
    const [widgetType] = widgetId.split('-') as [WidgetType, string]

    // Check if the widget can be spawned
    if (mosaicRootNode) {
      if (isToolWidgetType(widgetType)) {
        const existingWidgets = getLeaves(mosaicRootNode)
        const hasWidgetType = existingWidgets.some(id => {
          const [existingType] = (id as WidgetTypeWithSuffix).split('-')

          return existingType === widgetType
        })

        if (hasWidgetType) {
          return
        }
      } else {
        if (isKeyExistedInTree(mosaicRootNode, widgetId)) {
          dispatch(updateActiveWidgetWindowId(widgetId))

          return
        }
      }

      if (mosaicRootNode && getNumberOfLeaves(mosaicRootNode) >= MAX_WINDOW_COUNT) {
        dispatch(setToast({
          open: true,
          severity: 'error',
          title: 'Reached Window Limit',
          content: `You have reached the maximum limit of ${MAX_WINDOW_COUNT} open windows. Please close one or more windows to continue.`,
        }))
        
        return
      }

      // Create a new node structure that places the new widget in the top right
      const updatedRootNode: MosaicNode<WidgetTypeWithSuffix> = {
        direction: 'row',
        first: mosaicRootNode,
        second: widgetId,
        splitPercentage: 50,
      }

      setRootNode(updatedRootNode)
    } else {
      // If there's no existing layout, just create the widget
      setRootNode(widgetId)
    }

    dispatch(updateActiveWidgetWindowId(widgetId))
  }

  return { replaceWidgetWindow, closeWidgetWindows, spawnWidgetInTopRightCorner }
}